/**
 * 관 정보 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/layout/AdminLayout';
import DataTable, { Column } from '../../../components/common/DataTable';
import Pagination from '../../../components/common/Pagination';
import FilterBar, { FilterField } from '../../../components/common/FilterBar';
import { productService } from '../../../services/productService';
import { Hall, HallFilter } from '../../../types/product';

const HallsList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<HallFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['halls', filter],
    queryFn: () => productService.getHalls(filter),
  });

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  const columns: Column<Hall>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'name',
      label: '관명',
      render: (hall) => (
        <span
          style={{ cursor: 'pointer', color: '#00BCD4' }}
          onClick={() => navigate(`/admin/products/halls/${hall.id}`)}
        >
          {hall.name}
        </span>
      ),
    },
    {
      key: 'description',
      label: '설명',
      render: (hall) => (
        <span style={{ color: '#ccc' }}>
          {hall.description || '-'}
        </span>
      ),
    },
    {
      key: 'capacity',
      label: '수용인원',
      render: (hall) => (
        <span style={{ color: '#ccc' }}>
          {hall.capacity ? `${hall.capacity}명` : '-'}
        </span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (hall) => getStatusBadge(hall.status),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'search',
      label: '검색',
      type: 'text',
      placeholder: '관명으로 검색',
    },
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'active', label: '활성' },
        { value: 'inactive', label: '비활성' },
      ],
    },
  ];

  const handleFilterChange = (values: Record<string, any>) => {
    setFilter((prev) => ({
      ...prev,
      ...values,
      page: 1, // 필터 변경 시 첫 페이지로
    }));
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (hall: Hall) => {
    navigate(`/admin/products/halls/${hall.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>관 정보 관리</h1>
          <Button variant="primary" onClick={() => navigate('/admin/products/halls/new')}>
            관 등록
          </Button>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.halls || []}
              loading={isLoading}
              emptyMessage="관 정보가 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default HallsList;

